import { useTheme } from '@mui/material'
import GoogleMap, { LatLngLiteral, type Map as MapType, MapOptions } from 'google-maps-react-markers'
import { ReactNode, useEffect, useRef } from 'react'
import { getMapStyle } from './MapStyle'
import Marker from './Marker'
import { DEFAULT_GOOGLE_LANGUAGE, mapLocaleToGoogleMapsLanguage, useGoogleAPI } from '../../molecules/AddressSearch/useGoogleAPI'

export type OnGoogleApiLoadedType = (data: { map: google.maps.Map }) => void

export type Coords = LatLngLiteral

type MapProps = {
    center: LatLngLiteral
    apiKey: string
    language?: string
    setCenter?: (center: LatLngLiteral) => void
    defaultZoom?: number
    zoom?: number
    hasControl?: boolean
    onGoogleApiLoaded?: OnGoogleApiLoadedType
    children?: ReactNode
    options?: Partial<MapOptions>
}

export type MapTypeIds = 'roadmap' | 'satellite' | 'hybrid' | 'terrain'

const HORIZONTAL_BAR = 2 as google.maps.MapTypeControlStyle
const POSITION_BOTTOM_CENTER = 0 as google.maps.ControlPosition

export const Map = ({
    center,
    setCenter,
    apiKey,
    language = DEFAULT_GOOGLE_LANGUAGE,
    defaultZoom = 16,
    zoom,
    hasControl = true,
    children,
    options = {},
    onGoogleApiLoaded: onGoogleApiLoadedCallback,
}: MapProps) => {
    const theme = useTheme()
    const normalizedLanguage = mapLocaleToGoogleMapsLanguage(language)
    const { status } = useGoogleAPI(apiKey, normalizedLanguage)

    const mapRef = useRef<MapType | undefined>(undefined)

    const onGoogleApiLoaded = ({ map }: { map: MapType }) => {
        mapRef.current = map
        onGoogleApiLoadedCallback?.({ map })
    }
    const getMapOptions: () => MapOptions = () => {
        return {
            mapTypeControl: hasControl,
            streetViewControl: false,
            mapTypeId: 'roadmap' as const,
            mapTypeControlOptions: {
                style: HORIZONTAL_BAR,
                position: POSITION_BOTTOM_CENTER,
                mapTypeIds: ['roadmap', 'satellite', 'hybrid'],
            },
            styles: getMapStyle(theme),
            ...options,
        }
    }

    const handleDragEnd = (event: MouseEvent, { latLng: { lng, lat } }: { latLng: LatLngLiteral }) => {
        event.stopPropagation()
        const newCoordinates = { lat, lng }
        setCenter?.(newCoordinates)
    }

    useEffect(() => {
        if (mapRef.current) {
            mapRef.current.panTo(center)
            zoom && mapRef.current.setZoom(zoom)
        }
    }, [center, zoom])

    return (
        <GoogleMap
            apiKey={apiKey}
            defaultZoom={zoom || defaultZoom}
            defaultCenter={center}
            options={getMapOptions()}
            onGoogleApiLoaded={onGoogleApiLoaded}
            externalApiParams={{ language: normalizedLanguage }}
            loadScriptExternally
            idleContent={<div className="google-map" />}
            status={status}
        >
            {setCenter && <Marker lat={center.lat} lng={center.lng} onDragEnd={handleDragEnd} draggable />}
            {children}
        </GoogleMap>
    )
}

export default Map
